import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { style } from './button-input.style.js'
import { classMap } from 'lit/directives/class-map.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { formFieldValidationTypes } from 'elements/form-field/form-field.js'
import { buttonVariantTypes } from 'elements/button/button.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'


export class ButtonInputSubmit extends Event {
  static type = 'cse-button-input-submit' as const
  value: string = ''

  constructor(value: string) {
    super(ButtonInputSubmit.type, { bubbles: true, composed: true })
    this.value = value
  }
}

export class ButtonInputClear extends Event {
  static type = 'cse-button-input-clear' as const

  constructor() {
    super(ButtonInputClear.type, { bubbles: true, composed: true })
  }
}

@customElement('cse-button-input')
export class CSEButtonInput extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: String, attribute: 'button-variant' })
  buttonVariant: buttonVariantTypes[number] = 'secondary'

  @property({ type: Boolean })
  disabled: boolean = false

  @property()
  placeholder: string = ''

  @property({ type: String, reflect: true })
  value: string = ''

  @property({ type: String })
  valid: formFieldValidationTypes[number] = 'none'

  @property({ type: Boolean })
  override autofocus: boolean = false

  @property({ type: Boolean })
  loading: boolean = false

  @query('input')
  input!: HTMLInputElement

  override focus() {
    this.input.focus()
  }

  override render() {
    const classes = classMap({
      'cse-button-input': true,
      'disabled': this.disabled,
      'loading': this.loading,
      [`valid-${this.valid}`]: true,
    })

    return html`
      <div class="${classes}">
        <input
          .value="${this.value}"
          placeholder="${this.placeholder}"
          ?disabled="${this.disabled || this.loading}"
          ?autofocus="${this.autofocus}"
          @keyup="${this.handleTyping}"
        />
        <div class="actions">
          ${this.value.length > 0 && !this.disabled && !this.loading ? html`
            <cse-icon-button
              size="small"
              variant="secondary"
              @click="${this.clear}"
              aria-label="${t('clear_text', { ns: 'cse-button-input', default: 'Clear text' })}"
            ></cse-icon-button>
          ` : nothing}
          <slot name="suffix-icon"></slot>
          <cse-button variant="${this.buttonVariant}" size="small" @click="${this.submit}" ?disabled="${this.disabled}" ?loading="${this.loading}">
            <slot name="button-text"></slot>
          </cse-button>
        </div>
      </div>
    `
  }

  handleTyping(e: KeyboardEvent) {
    this.value = (e.target as HTMLInputElement).value
    if (e.code === 'Enter' || e.code === 'NumpadEnter') {
      this.submit(new Event('click'))
    }
  }

  clear(e: Event) {
    e.stopPropagation()
    this.value = ''
    this.dispatchEvent(new ButtonInputClear())
  }

  submit(e: Event) {
    e.stopPropagation()
    this.dispatchEvent(new ButtonInputSubmit(this.value || ''))
  }
}
